import { useSessionStorage } from '@uidotdev/usehooks';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
//@ts-ignore
import Card from '../Reusable/Card';
//@ts-ignore
import { tw } from '@careinfo/tailwind';
import APIS from '../../constant/EndPoint';
import { IFilterManifest } from '../../types/interface';
import { hasValues } from '../../utils/hasValues';
import ManifestFilter from '../ManifestFilter';
import DataTable from '../Reusable/DataTable';
import NACell from '../Reusable/DataTable/NACell';
import { classNames } from '../Reusable/DatePicker/helpers';

const ManifestList = (props: any) => {
  const { defaultBranch, ConsignmentData } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParms] = useSearchParams();
  const [localState, handleSetState] = useSessionStorage<IFilterManifest>(
    'dispatch-manifest-filter',
    {
      created__range: null,
      vehicle: null,
      driver: null,
      destination_branch: null,
    }
  );
  const [active, setActive] = useState('');
  const [isFilterVisible, setFilterVisiable] = useState(hasValues(localState));
  const { setValue, getValues, watch, reset, control } =
    useForm<IFilterManifest>({
      defaultValues: async (payload) => ({
        created__range: localState?.created__range ?? null,
        vehicle: localState?.vehicle ?? null,
        driver: localState?.driver ?? null,
        destination_branch: localState?.destination_branch ?? null,
      }),
    });

  const handleFilterReset = () => {
    reset({
      created__range: null,
      vehicle: null,
      driver: null,
      destination_branch: null,
    });
  };

  const checkedAllItems = searchParams.get('selectAllItems') === '1';
  const handleIndividualSelect = (value: string) => {
    let selectedItems = searchParams.get('selectedItems')?.split(',');
    if (!selectedItems || selectedItems === undefined) {
      searchParams.set('selectedItems', value);
      setSearchParms(searchParams);
      return;
    }
    let itemIndex = selectedItems?.findIndex((item) => item === value);
    if (itemIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(',')
        .toString();
      if (newSelectedItems.length) {
        searchParams.set('selectedItems', newSelectedItems);
      } else {
        searchParams.delete('selectedItems');
      }
      searchParams.set('selectAllItems', '0');
      setSearchParms(searchParams);
    } else {
      selectedItems?.push(value);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(',')
        .toString();
      searchParams.set('selectedItems', newSelectedItems);
      setSearchParms(searchParams);
    }
  };

  const isSelected = (value: string) => {
    const selectedItems = searchParams.get('selectedItems')?.split(',');
    if (!selectedItems) {
      return false;
    }
    return selectedItems.includes(value);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const isSelected = searchParams.get('selectAllItems');
    if (isSelected === '1') {
      searchParams.set('selectAllItems', '0');
      searchParams.delete('selectedItems');
      setSearchParms(searchParams);
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(',')
        .toString();
      searchParams.set('selectAllItems', '1');
      searchParams.set('selectedItems', selectedItems);
      setSearchParms(searchParams);
    }
  };

  const defaultColumns: any = [
    {
      id: 'select',
      header: ({ table }: any) => {
        return (
          <input
            type='checkbox'
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ row }: any) => (
        <input
          type='checkbox'
          checked={isSelected(row?.original?.id)}
          onChange={() => handleIndividualSelect(row?.original?.id)}
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      header: 'WayBill',
      accessorKey: 'tracking_number',
      cell: NACell,
    },
    {
      header: 'Created',
      accessorKey: 'created',
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>
              {getValue()
                ? moment(getValue()).format('YY-MM-DD HH:mm:ss')
                : 'N/A'}
            </p>
          </>
        );
      },
    },
    {
      header: 'Updated',
      accessorKey: 'modified',
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>
              {getValue()
                ? moment(getValue()).format('YY-MM-DD HH:mm:ss')
                : 'N/A'}
            </p>
          </>
        );
      },
    },
    {
      header: 'Transport',
      accessorKey: 'destination',
      cell: NACell,
    },
    {
      header: 'Driver',
      accessorKey: 'driver.user',
      cell: NACell,
    },
    {
      header: 'Total Items',
      accessorKey: 'total_items',
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? 0.0}</p>
          </>
        );
      },
    },
  ];

  const handleDetailNavigate = (row: any) => {
    navigate(`edit/${row?.original?.id}`);
  };

  const filterParams = watch();

  useEffect(() => {
    handleSetState({
      ...localState,
      ...filterParams,
    });
  }, [filterParams]); //eslint-disable-line

  const params = useCallback(() => {
    const newValue = {};
    for (const [key, value] of Object.entries(filterParams)) {
      if (value) {
        if (Array.isArray(value)) {
          newValue[key] = value.map((v) => v.value).join(',');
        } else if (typeof value === 'object') {
          newValue[key] = value.id;
        } else if (!value) {
          newValue[key] = null;
        } else {
          newValue[key] = value;
        }
      }
    }
    return newValue;
  }, [filterParams]);

  return (
    <>
      <Card title='Filters' onClick={() => setFilterVisiable(!isFilterVisible)}>
        <ManifestFilter
          isFilterVisible={isFilterVisible}
          setValue={setValue}
          getValues={getValues}
          reset={handleFilterReset}
          control={control}
        />
      </Card>
      <DataTable
        url={APIS.COMBINE_ORDERS}
        key={'Combine Order'}
        queryKey={'manifest'}
        params={{
          type: 'dispatch',
          parent__isnull: true,
          ...params(),
        }}
        handleDetailNavigate={handleDetailNavigate}
        columns={defaultColumns}
        // orderSearch={true}
        tableName="conbine-order"
        isDownload={true}
        actionMenu={<></>}
        addNewPagePath={'/create'}
      />
    </>
  );
};

export default ManifestList;
